<template>
  <div>
    <!-- 合作方管理页面 -->
    <basic-container>
      <div v-if="currentView === 'list'" class="partner-manage-table-container">
        <!-- Tab导航 -->
        <div class="tabs">
            <el-tabs :value="activeTab" @tab-click="handleTabClick">
            <el-tab-pane name="online" label="线上数据"></el-tab-pane>
            <el-tab-pane name="local" label="本地数据"></el-tab-pane>
            </el-tabs>
        </div>

        <!-- 搜索栏 -->
        <Search 
            ref="searchForm" 
            @search="handleSearch" 
            @add="handleAdd"
            :searchForm="searchForm"
            :loading="loading"
            :isLocal="isLocal"
        />
        
        <!-- 合作方列表 -->
        <div class="partner-table-container">
          <PartnerTable 
            ref="partnerTable" 
            :tableData="partnerData" 
            :loading="loading" 
            :isLocal="isLocal"
            @edit="handleEdit"
            @delete="handleDelete"
            @submit="handleSubmit"
            @revoke="handleSubmit"
            @log="handleLog"
            />
        </div>

        <!-- 分页组件 -->
        <div class="pagination-container">
            <ElementPagination
            :current-page="page.current"
            :page-size="page.size"
            :page-sizes="[10, 20, 50, 100]"
            :total="page.total"
            @size-change="handleSizeChange"
            @current-change="handleCurrentChange"
            />
        </div>
      </div>

      <!-- 新增/编辑页面 -->
      <PartnerEdit
        v-if="currentView === 'edit'"
        :type="componentProps.type"
        :id="componentProps.id"
        :formData="editFormData"
        @back="handleBack"
        @add="saveAddPartner"
        @edit="saveEditPartner"
      />

      <!-- 日志弹窗 -->
      <OptionLog 
        ref="optionLog" 
        :columnConfig="optionLogColumn" 
        @reload="reloadLog"
        :closeBtn="false"
        title="操作日志"
      >
        <template #type="{ row }">
          <div>
            <span v-if="row.type === 1">新增</span>
            <span v-if="row.type === 2">修改</span>
            <span v-if="row.type === 3">删除</span>
            <span v-if="row.type === 4">上线</span>
            <span v-if="row.type === 5">下线</span>
            <span v-if="row.type === 6">提交审核</span>
            <span v-if="row.type === 7">撤销审核</span>
          </div>
        </template>
      </OptionLog>
    </basic-container>
  </div>
</template>

<script>
import Search from "./components/search.vue";
import PartnerTable from "./components/partnerTable.vue";
import PartnerEdit from './PartnerEdit.vue';
import ElementPagination from '@/components/ElementPagination';
import OptionLog from '@/components/OptionsLog/optionLog.vue';
import { getLogList } from '@/api/tv/log';
import { getPartnerLocalList, getPartnerOnlineList, addPartner, editPartner, deletePartner, submitForReview } from "@/api/financialManage/partnerManage";
import { cloneDeep } from 'lodash';

export default {
  name: "PartnerManage",
  components: {
    Search,
    PartnerTable,
    PartnerEdit,
    ElementPagination,
    OptionLog
  },
  data() {
    return {
      loading: false,
      partnerData: [],
      page: {
        current: 1,
        size: 10,
        total: 0
      },
      // Tab状态管理
      activeTab: 'local', // 'online' 或 'local'
      // 搜索表单
      searchForm: {
        partnerName: "", // 现在用于搜索合作方名称和公司名称
        channelIds: [], // 改为多选，使用数组
        partnerType: "", // 合作类型：1-合作方 2-合伙人
        status: "",
        reviewStatus: ""
      },
      // 页面状态管理
      currentView: 'list', // 'list' 或 'edit'
      componentProps: {
        type: 'add', // 'add' 或 'edit'
        id: ''
      },
      editFormData: {
        partnerName: '',
        companyName: '',
        status: '1',
        partnerType: 1, // 合作类型：1-合作方 2-合伙人
        partnershipId: '', // 合作人主键id（当partnerType为2时使用）
        linkmanName: '', // 联系人姓名（当partnerType为2时使用）
        linkmanIdCard: '', // 联系人身份证号（当partnerType为2时使用）
        settlementInfo: [] // 结算信息列表
      },
      // 日志列配置
      optionLogColumn: [
        {
          prop: "operateTime",
          label: "操作时间",
        },
        {
          prop: "operator",
          label: "操作人",
        },
        {
          prop: "type",
          label: "操作类型",
          slot: "type",
        },
      ],
    };
  },
  computed: {
    // 是否为本地数据模式
    isLocal() {
      return this.activeTab === 'local';
    }
  },
  created() {
    this.getPartnerList();
  },
  methods: {
    // 获取默认结算方式：企业默认手动结算(2)，合伙人默认自动结算(1)
    getDefaultSettlementType(partnerType) {
      return partnerType === 2 ? 1 : 2;
    },
    
    // 获取合作方列表
    async getPartnerList() {
      const requestParams = {
        current: this.page.current,
        size: this.page.size,
        state: this.searchForm.status,
        partnerName: this.searchForm.partnerName,
        reviewStatus: this.searchForm.reviewStatus,
        partnerType: this.searchForm.partnerType,
        channelIds: this.searchForm.channelIds.length > 0 ? this.searchForm.channelIds.join(',') : ''
      };
      
      console.log('请求参数:', requestParams);
    
      this.loading = true;
      try {
        const { data } = this.activeTab === 'local' ? await getPartnerLocalList(requestParams) : await getPartnerOnlineList(requestParams);
        
        console.log('获取合作方列表数据:', data);

        if(data.code === 200) {
          const list = data.data.records.map(item => {
            return {
              companyName: item.partnerCompanyName || '',
              accountNumber: item.collectionAccount || '',
              accountInfo: item.openAccountInfo || '',
              bankName: item.openAccountBank || '',
              contactPerson: item.linkman || '',
              contactEmail: item.contactMail || '',
              status: item.state,
              partnerType: item.partnerType || 1, // 合作类型：1-合作方 2-合伙人
              partnershipId: item.partnershipId || '', // 合作人主键id
              linkmanName: item.linkmanName || '', // 联系人姓名
              linkmanIdCard: item.linkmanIdCard || '', // 联系人身份证号
              settlementInfo: item.settleConfList.map(settleItem => {
                const partnerType = item.partnerType || 1;
                const defaultSettlementType = this.getDefaultSettlementType(partnerType);
                return {
                  ...settleItem,

                  channelIds: settleItem.appIdList,
                  channels: this.getChannels(settleItem),
                  ratio: (settleItem.settlementRatio !== null && settleItem.settlementRatio !== undefined) ? settleItem.settlementRatio : '',
                  passRate: (settleItem.channelFeeRatio !== null && settleItem.channelFeeRatio !== undefined) ? settleItem.channelFeeRatio : '',
                  settlementType: settleItem.settlementType || defaultSettlementType, // 结算方式：1-自动结算 2-手动结算
                }
              }) || [],
              ...item
            }
          });

          this.page.total = data.data.total;
          if (this.page.total > 0 &&  list.length === 0) {
            this.page.current = this.page.current - 1;
            this.getPartnerList();
            return;
          }

          this.partnerData = list.map(item => {
            return {
              ...item,
              expanded: false // 控制子列表展开状态
            };
          });
        } else {
          this.$message.error(data.msg);
        }
      } catch (error) {
        console.error('获取列表失败:', error);
        this.$message.error('获取列表失败');
      } finally {
        this.loading = false;
      }
    },

    getChannels(settleItem) {
      return settleItem.channelList.map((channelItem, index) => {
        const channelRatio = settleItem.channelRatioList[index];
        return {
          ...channelItem,
          channelId: channelItem.channelId,
          channelName: channelItem.channelName,
          ratio: settleItem.calculationMethod === '1' ? '' : (channelRatio.settlementRatio !== null && channelRatio.settlementRatio !== undefined) ? channelRatio.settlementRatio : '',
          passRate: settleItem.calculationMethod === '1' ? '' : (channelRatio.channelFeeRatio !== null && channelRatio.channelFeeRatio !== undefined) ? channelRatio.channelFeeRatio : '',
        }
      }) || [];
    },
    
    // 搜索处理
    handleSearch(params) {
      console.log('搜索参数:', params);
      this.page.current = 1;
      this.searchForm = params;
      this.getPartnerList();
    },
    
    // 新增处理
    handleAdd() {
      console.log('新增合作方');
      this.currentView = 'edit';
      this.componentProps = {
        type: 'add'
      };
      this.resetEditForm();
    },
    
    // 编辑处理
    handleEdit(row) {
      console.log('编辑合作方:', row);
      this.currentView = 'edit';
      this.componentProps = {
        type: 'edit',
        id: row.id
      };
      this.editFormData = {
        id: row.id,
        partnerName: row.partnerName || '',
        companyName: row.companyName || '',
        status: row.status,
        partnerType: row.partnerType || 1,
        partnershipId: row.partnershipId || '',
        linkmanName: row.linkmanName || '',
        linkmanIdCard: row.linkmanIdCard || '',
        settlementInfo: row.settlementInfo || [],
        accountNumber: row.accountNumber || '',
        accountInfo: row.accountInfo || '',
        bankName: row.bankName || '',
        contactPerson: row.contactPerson || '',
        contactEmail: row.contactEmail || ''
      };
    },

    // 删除处理
    async handleDelete(row) {
      console.log('删除合作方:', row);

      try {
        await this.$confirm('确认删除该合作方吗？', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        });

        const { data } = await deletePartner({ id: row.id });
        
        if (data.code === 200) {
          this.$message.success('删除成功');
          this.getPartnerList();
        } else {
          this.$message.error(data.msg || '删除失败');
        }
      } catch (error) {
        if (error !== 'cancel') {
          console.error('删除失败:', error);
          this.$message.error('删除失败');
        }
      }
    },

    // 提交/撤销审核
    async handleSubmit(rowInfo) {
      console.log('提交审核:', rowInfo);
      const { row, type } = rowInfo;
      console.log('提交审核:', rowInfo, row, type);

      try {
        this.$confirm(type === 1 ? '确认提交审核吗？' : '确认撤销审核吗？', '提示', {
          confirmButtonText: '确定',
          cancelButtonText: '取消',
          type: 'warning'
        }).then(async () => {
          const { data } = await submitForReview({ id: row.id, type: type });
          
          if (data.code === 200) {
            this.$message.success(type === 1 ? '提交审核成功' : '撤销审核成功');
            this.getPartnerList();
          } else {
            this.$message.error(data.msg || (type === 1 ? '提交审核失败' : '撤销审核失败'));
          }
        }).catch(() => {
          this.$message.info(type === 1 ? '取消提交审核' : '取消撤销审核');
        });
      } catch (error) {
        console.error(type === 1 ? '提交审核失败:' : '撤销审核失败:', error);
        this.$message.error(type === 1 ? '提交审核失败' : '撤销审核失败');
      }
    },

    // 返回列表页面
    handleBack() {
      this.currentView = 'list';
      this.componentProps = {};
      this.resetEditForm();
    },

    //格式化数据
    formatData(form) {
      const partnerType = form.partnerType || 1;
      const defaultSettlementType = this.getDefaultSettlementType(partnerType);
      const params = { 
        partnerCompanyName: form.companyName,
        partnerName: form.partnerName,
        state: form.status,
        settleConfList: form.settlementInfo.map(item => {
          return {
            ...item,
            channelFeeRatio: item.passRate,
            settlementRatio: item.ratio,
            calculationMethod: item.calculationMethod,
            paymentMethod: item.paymentMethod,
            remark: item.remark,
            settlementType: item.settlementType || defaultSettlementType, // 结算方式：1-自动结算 2-手动结算
            appIdList: item.channelIds,
            channelRatioList: item.channels.map(channel => {
              return {
                ...channel,
                channelId: channel.channelId,
                channelName: channel.channelName,
                settlementRatio: channel.ratio,
                channelFeeRatio: channel.passRate,
              };
            }),
          };
        }),
        partnerType: form.partnerType || 1,
        partnershipId: form.partnershipId || '',
        linkmanName: form.linkmanName || '',
        linkmanIdCard: form.linkmanIdCard || '',
        collectionAccount: form.accountNumber,
        openAccountInfo: form.accountInfo,
        openAccountBank: form.bankName,
        linkman: form.contactPerson,
        contactMail: form.contactEmail,
        ...form,
        };

        return params;
    },
    // 新增合作方
    async saveAddPartner(form) {
      try {
        console.log('新增合作方', form);
        const params = this.formatData(form);

        const { data } = await addPartner(params);
        if (data.code === 200) {
          this.$message.success('新增成功');
          this.handleBack();
          this.getPartnerList();
        }
      } catch (error) {
        console.error('新增合作方失败:', error);
        this.$message.error('新增合作方失败');
      }
    },

    // 编辑合作方
    async saveEditPartner(form) {
      try {
      console.log('编辑合作方', form);
      const params = this.formatData(form);
      const { data } = await editPartner(form.id, params);
      console.log('编辑合作方的响应', data);
        if (data.code === 200) {
          this.$message.success('编辑成功');
            this.handleBack();
            this.getPartnerList();
          }
        } catch (error) {
          console.error('编辑合作方失败:', error);
          this.$message.error('编辑合作方失败');
        }
    },


    // 重置编辑表单
    resetEditForm() {
      this.editFormData = {
        partnerName: '',
        companyName: '',
        status: '1',
        partnerType: 1,
        partnershipId: '',
        linkmanName: '',
        linkmanIdCard: '',
        settlementInfo: [],
        accountNumber: '',
        accountInfo: '',
        bankName: '',
        contactPerson: '',
        contactEmail: ''
      };
    },

    // 分页处理
    handleSizeChange(val) {
      this.page.size = val;
      this.page.current = 1;
      this.getPartnerList();
    },

    handleCurrentChange(val) {
      this.page.current = val;
      this.getPartnerList();
    },

    // Tab切换处理
    handleTabClick(tab) {
      console.log('切换Tab:', tab.name);
      this.activeTab = tab.name;
      this.page.current = 1; // 重置页码
      this.getPartnerList();
    },

    // 日志处理
    async handleLog(row) {
      console.log('查看日志:', row);
      try {
        const optSearch = this.$refs.optionLog.searchParams;
        const { data } = await getLogList({
          ...optSearch,
          subjectId: row.id,
          module: 'FINANCE_PARTNER_MANAGE_LOCAL'
        });
        console.log("查看日志的响应", data);
        if (data.code === 200) {
          const list = cloneDeep(data.data.records || []);
          optSearch.total = data.data.total || 0;
          this.$refs.optionLog.show(list, row.id);
        } else {
          this.$message.error(data.msg || '获取日志失败');
        }
      } catch (error) {
        console.error('获取日志失败:', error);
        this.$message.error('获取日志失败');
      }
    },

    // 日志分页
    async reloadLog(id) {
      console.log("日志分页", id);
      try {
        const optSearch = this.$refs.optionLog.searchParams;
        const { data } = await getLogList({
          ...optSearch,
          subjectId: id,
          module: 'FINANCE_PARTNER_MANAGE_LOCAL'
        });
        console.log("查看日志的响应", data);
        if (data.code === 200) {
          const list = cloneDeep(data.data.records || []);
          this.$refs.optionLog.list = list;
          this.$refs.optionLog.searchParams.total = data.data.total || 0;
        } else {
          this.$message.error(data.msg || '获取日志失败');
        }
      } catch (error) {
        console.error("日志分页失败", error);
        this.$message.error('获取日志失败');
      }
    },


  }
};
</script>

<style lang="scss" scoped>
.tabs {
  margin-bottom: 20px;
  
  /deep/ .el-tabs__header {
    margin-bottom: 0;
  }
  
  /deep/ .el-tabs__content {
    display: none;
  }
}

.partner-table-container {
  margin: 20px 0;
}

.pagination-container {
  margin-top: 20px;
}
</style>

